<template>
  <HeaderCard>
    <template v-slot:header>
      <div class="grid grid-cols-2 gap-sm">
        <div class="self-center">
          <slot
            name="header"
            :maximise="maximise"
            :minimise="minimise"
            :toggle="toggle"
            :isExpanded="isExpanded"
          />
        </div>
        <div class="self-center place-self-end">
          <slot
            name="controls"
            :maximise="maximise"
            :minimise="minimise"
            :toggle="toggle"
            :isExpanded="isExpanded"
          >
            <div class="flex gap-sm items-center">
              <MinimiseControl
                v-show="toggleable ? isExpanded : true"
                :disabled="!isExpanded"
                @minimised="minimise"
                data-test="minimise-control"
              />
              <MaximiseControl
                v-show="toggleable ? !isExpanded : true"
                :disabled="isExpanded"
                @maximised="maximise"
                data-test="maximise-control"
              />
            </div>
          </slot>
        </div>
      </div>
    </template>

    <template v-slot:content>
      <div data-slot="content">
        <slot
          name="content"
          :maximise="maximise"
          :minimise="minimise"
          :toggle="toggle"
          :isExpanded="isExpanded"
        />
      </div>
      <div
        v-show="isExpanded"
        data-test="expanded-content">
        <slot
          name="expandedContent"
          :maximise="maximise"
          :minimise="minimise"
          :toggle="toggle"
          :isExpanded="isExpanded"
        />
      </div>
    </template>
  </HeaderCard>
</template>

<script>
import { defineComponent, ref, watchEffect } from 'vue';
import { HeaderCard } from '@/components/library/cards/HeaderCard';
import { MaximiseControl, MinimiseControl } from '@/components/ui/common/cardControls';
import { createEventLogger } from '@/utils/analytics';

/**
 * Each slot is passed the same data and functions. It may seem like overkill
 * but it provides a lot of flexibility when extending/wrapping this component.
 */
export default defineComponent({
  name: 'ExpandableCard',
  components: {
    HeaderCard,
    MaximiseControl,
    MinimiseControl,
  },
  props: {
    expanded: {
      type: Boolean,
      required: false,
      default: false,
    },
    toggleable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * This is the equivalent of data-ec. If it's empty,
     * nothing will be logged in this component.
     */
    logEventCategory: {
      type: String,
      required: false,
    },
    logMinimiseEventName: {
      type: String,
      required: false,
      default: 'minimised_card',
    },
    logMaximiseEventName: {
      type: String,
      required: false,
      default: 'maximised_card',
    },
    logToggleEventName: {
      type: String,
      required: false,
      default: 'toggled_card',
    },
  },
  emits: [
    'minimised',
    'maximised',
    'toggled',
  ],
  setup(props, { emit }) {
    const isExpanded = ref(props.expanded);

    const logEvent = createEventLogger(props.logEventCategory);

    watchEffect(() => {
      isExpanded.value = props.expanded;
    });

    function minimise() {
      if (isExpanded.value === false) {
        return;
      }
      isExpanded.value = false;
      emit('minimised');
      emit('toggled', isExpanded.value);
      logEvent(props.logMinimiseEventName);
    }

    function maximise() {
      if (isExpanded.value === true) {
        return;
      }
      isExpanded.value = true;
      emit('maximised');
      emit('toggled', isExpanded.value);
      logEvent(props.logMaximiseEventName);
    }

    function toggle() {
      isExpanded.value = !isExpanded.value;
      emit('toggled', isExpanded.value);
      logEvent(props.logToggleEventName);
    }

    return {
      minimise,
      maximise,
      toggle,
      isExpanded,
    };
  },
});
</script>
